import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { useScheduledNotifications } from '../hooks/useScheduledNotifications';
import { SendNotificationModal } from '../components/SendNotificationModal';
import { ScheduledNotificationModal } from '../components/ScheduledNotificationModal';
import { ScheduledNotificationHistoryModal } from '../components/ScheduledNotificationHistoryModal';
import { DriverInactivityAlertPanel } from '../components/DriverInactivityAlertPanel';
import '../styles/notifications.scss';
import { FaPlus, FaTrash, FaCheckCircle, FaTimesCircle, FaUser, FaCar, FaPen, FaPause, FaPlay, FaHistory } from 'react-icons/fa';

const REPEAT_DAY_NAMES = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

const describeSchedule = (schedule) => {
    switch (schedule.repeatType) {
        case 'once':
            return 'مرة واحدة';
        case 'daily':
            return schedule.dayInterval > 1 ? `يوميًا (كل ${schedule.dayInterval} أيام)` : 'يوميًا';
        case 'weekly':
            return `أسبوعيًا - يوم ${REPEAT_DAY_NAMES[schedule.dayOfWeek] || ''}`;
        case 'monthly':
            return `شهريًا - يوم ${schedule.dayOfMonth} من كل شهر`;
        default:
            return '';
    }
};

const SCHEDULE_STATUS_TEXT = {
    active: 'نشطة',
    paused: 'متوقفة مؤقتًا',
    completed: 'مكتملة',
};

export const Notifications = () => {
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const {
        notifications,
        stats,
        isLoading,
        fetchNotifications,
        deleteNotification
    } = useNotifications();

    const {
        scheduledNotifications,
        isLoading: isScheduledLoading,
        fetchScheduledNotifications,
        toggleScheduledNotificationStatus,
        deleteScheduledNotification
    } = useScheduledNotifications();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [typeFilter, setTypeFilter] = useState('all');
    const [deleteLoading, setDeleteLoading] = useState('');

    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [historySchedule, setHistorySchedule] = useState(null);
    const [scheduleStatusLoading, setScheduleStatusLoading] = useState('');
    const [scheduleDeleteLoading, setScheduleDeleteLoading] = useState('');

    const isScheduledTab = typeFilter === 'scheduled';
    const isInactivityAlertTab = typeFilter === 'driver-inactivity';

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
    }, [user]);

    const handleTypeFilter = (type) => {
        setTypeFilter(type);
        if (type === 'scheduled') {
            fetchScheduledNotifications();
        } else if (type !== 'driver-inactivity') {
            fetchNotifications(type);
        }
    };

    const handleOpenModal = () => {
        if (isScheduledTab) {
            setEditingSchedule(null);
            setIsScheduleModalOpen(true);
        } else if (!isInactivityAlertTab) {
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        fetchNotifications(typeFilter);
    };

    const handleCloseScheduleModal = () => {
        setIsScheduleModalOpen(false);
        setEditingSchedule(null);
    };

    const handleEditSchedule = (schedule) => {
        setEditingSchedule(schedule);
        setIsScheduleModalOpen(true);
    };

    const handleToggleScheduleStatus = async (schedule) => {
        const nextStatus = schedule.status === 'paused' ? 'active' : 'paused';
        setScheduleStatusLoading(schedule._id);
        try {
            await toggleScheduledNotificationStatus(schedule._id, nextStatus);
        } catch (error) {
            console.error('Error toggling schedule status:', error);
        } finally {
            setScheduleStatusLoading('');
        }
    };

    const handleDeleteSchedule = async (scheduleId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه الجدولة؟ سيتم حذف سجل الإرسال الخاص بها أيضًا')) {
            return;
        }

        setScheduleDeleteLoading(scheduleId);
        try {
            await deleteScheduledNotification(scheduleId);
        } catch (error) {
            console.error('Error deleting schedule:', error);
        } finally {
            setScheduleDeleteLoading('');
        }
    };

    const handleDelete = async (notificationId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا الإشعار؟')) {
            return;
        }

        setDeleteLoading(notificationId);
        try {
            await deleteNotification(notificationId);
        } catch (error) {
            console.error('Error deleting notification:', error);
        } finally {
            setDeleteLoading('');
        }
    };

    const getTypeText = (type) => {
        return type === 'for-users' ? 'للمستخدمين' : 'للسائقين';
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('ar-LY', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading && !isScheduledTab && !isInactivityAlertTab) {
        return (
            <section className='container notifications'>
                <div className="loading">جاري التحميل...</div>
            </section>
        );
    }

    return (
        <section className='container notifications'>
            <div className="header">
                <div className="title">
                    <h1 className="TXT-heading color-normal">الإشعارات</h1>
                    <p className="TXT-normal color-light">إدارة وإرسال الإشعارات</p>
                </div>
                {!isInactivityAlertTab && (
                    <button
                        className="create-btn"
                        onClick={handleOpenModal}
                    >
                        <FaPlus />
                        <span>{isScheduledTab ? 'إنشاء جدولة' : 'إنشاء إشعار'}</span>
                    </button>
                )}
            </div>

            {!isScheduledTab && !isInactivityAlertTab && stats && (
                <div className="stats-cards">
                    <div className="stat-card">
                        <h3>إجمالي الإشعارات</h3>
                        <p className="stat-value">{stats.total}</p>
                    </div>
                    <div className="stat-card successful">
                        <h3>الإشعارات الناجحة</h3>
                        <p className="stat-value">{stats.successful}</p>
                    </div>
                    <div className="stat-card failed">
                        <h3>الإشعارات الفاشلة</h3>
                        <p className="stat-value">{stats.failed}</p>
                    </div>
                    <div className="stat-card users">
                        <h3>للمستخدمين</h3>
                        <p className="stat-value">{stats.forUsers}</p>
                    </div>
                    <div className="stat-card drivers">
                        <h3>للسائقين</h3>
                        <p className="stat-value">{stats.forDrivers}</p>
                    </div>
                </div>
            )}

            <div className="filters-section">
                <div className="type-filters">
                    <button 
                        className={`filter-btn ${typeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => handleTypeFilter('all')}
                    >
                        الكل
                    </button>
                    <button 
                        className={`filter-btn ${typeFilter === 'for-users' ? 'active' : ''}`}
                        onClick={() => handleTypeFilter('for-users')}
                    >
                        للمستخدمين
                    </button>
                    <button
                        className={`filter-btn ${typeFilter === 'for-drivers' ? 'active' : ''}`}
                        onClick={() => handleTypeFilter('for-drivers')}
                    >
                        للسائقين
                    </button>
                    <button
                        className={`filter-btn ${isScheduledTab ? 'active' : ''}`}
                        onClick={() => handleTypeFilter('scheduled')}
                    >
                        مجدولة
                    </button>
                    <button
                        className={`filter-btn ${isInactivityAlertTab ? 'active' : ''}`}
                        onClick={() => handleTypeFilter('driver-inactivity')}
                    >
                        تنبيه للسائقين
                    </button>
                </div>
            </div>

            {isInactivityAlertTab ? (
                <DriverInactivityAlertPanel />
            ) : isScheduledTab ? (
                isScheduledLoading ? (
                    <div className="loading">جاري التحميل...</div>
                ) : scheduledNotifications.length === 0 ? (
                    <p className="empty-state">لا توجد إشعارات مجدولة بعد</p>
                ) : (
                    <div className="scheduled-notifications-grid">
                        {scheduledNotifications.map(schedule => (
                            <div key={schedule._id} className="schedule-card">
                                <div className="card-header">
                                    <div className="type-badge">
                                        {schedule.notificationType === 'for-users' ? <FaUser /> : <FaCar />}
                                        <span>{getTypeText(schedule.notificationType)}</span>
                                    </div>
                                    <div className={`status-badge ${schedule.status}`}>
                                        {SCHEDULE_STATUS_TEXT[schedule.status]}
                                    </div>
                                </div>

                                <div className="card-body">
                                    <h3 className="schedule-name">{schedule.name}</h3>
                                    <p className="schedule-repeat">{describeSchedule(schedule)} - الساعة {schedule.sendTime}</p>

                                    <h4 className="schedule-content-title">{schedule.title}</h4>
                                    <p className="schedule-content-body">{schedule.body}</p>

                                    <div className="date-info">
                                        <span className="date-label">{schedule.status === 'completed' ? 'تم الإرسال في:' : 'الإرسال القادم:'}</span>
                                        <span className="date-value">
                                            {schedule.status === 'completed'
                                                ? (schedule.lastRunAt ? formatDate(schedule.lastRunAt) : '—')
                                                : (schedule.nextRunAt ? formatDate(schedule.nextRunAt) : '—')}
                                        </span>
                                    </div>
                                    <div className="date-info">
                                        <span className="date-label">عدد مرات الإرسال:</span>
                                        <span className="date-value">{schedule.totalRuns || 0}</span>
                                    </div>
                                </div>

                                <div className="card-footer">
                                    <button
                                        className="action-btn history-btn"
                                        onClick={() => setHistorySchedule(schedule)}
                                        title="السجل"
                                    >
                                        <FaHistory />
                                    </button>
                                    <button
                                        className="action-btn edit-btn"
                                        onClick={() => handleEditSchedule(schedule)}
                                        title="تعديل"
                                    >
                                        <FaPen />
                                    </button>
                                    {schedule.status !== 'completed' && (
                                        <button
                                            className="action-btn pause-btn"
                                            onClick={() => handleToggleScheduleStatus(schedule)}
                                            disabled={scheduleStatusLoading === schedule._id}
                                            title={schedule.status === 'paused' ? 'استئناف' : 'إيقاف مؤقت'}
                                        >
                                            {scheduleStatusLoading === schedule._id ? (
                                                <span className="loading-spinner"></span>
                                            ) : schedule.status === 'paused' ? <FaPlay /> : <FaPause />}
                                        </button>
                                    )}
                                    <button
                                        className="action-btn delete-btn"
                                        onClick={() => handleDeleteSchedule(schedule._id)}
                                        disabled={scheduleDeleteLoading === schedule._id}
                                        title="حذف"
                                    >
                                        {scheduleDeleteLoading === schedule._id ? (
                                            <span className="loading-spinner"></span>
                                        ) : <FaTrash />}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
            <div className="notifications-grid">
                {notifications.map(notification => (
                    <div key={notification._id} className="notification-card">
                        <div className="card-header">
                            <div className="type-badge">
                                {notification.notificationType === 'for-users' ? <FaUser /> : <FaCar />}
                                <span>{getTypeText(notification.notificationType)}</span>
                            </div>
                            <div className="status-icon">
                                {notification.isSuccessful ? (
                                    <FaCheckCircle className="success" />
                                ) : (
                                    <FaTimesCircle className="failed" />
                                )}
                            </div>
                        </div>

                        <div className="card-body">
                            <h3 className="notification-title">{notification.title}</h3>
                            <p className="notification-body">{notification.body}</p>

                            {notification.isForOneUser && (
                                <div className="target-user">
                                    <h4>المستلم:</h4>
                                    <p className="user-name">{notification.userName}</p>
                                    <p className="user-phone">{notification.userPhoneNumber}</p>
                                </div>
                            )}

                            <div className="date-info">
                                <span className="date-label">تاريخ الإرسال:</span>
                                <span className="date-value">{formatDate(notification.createdAt)}</span>
                            </div>
                        </div>

                        <div className="card-footer">
                            <button 
                                className="delete-btn"
                                onClick={() => handleDelete(notification._id)}
                                disabled={deleteLoading === notification._id}
                            >
                                {deleteLoading === notification._id ? (
                                    <span className="loading-spinner"></span>
                                ) : (
                                    <>
                                        <FaTrash />
                                        <span>حذف</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            )}

            <SendNotificationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />

            <ScheduledNotificationModal
                isOpen={isScheduleModalOpen}
                onClose={handleCloseScheduleModal}
                schedule={editingSchedule}
            />

            <ScheduledNotificationHistoryModal
                isOpen={Boolean(historySchedule)}
                onClose={() => setHistorySchedule(null)}
                schedule={historySchedule}
            />
        </section>
    );
};

export default Notifications;